import React from 'react';

import { Button, Divider, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import useCreateDiscount from 'hooks/discount/useCreateDiscount';
import useDiscount from 'hooks/discount/useDiscount';
import useUpdateDiscount from 'hooks/discount/useUpdateDiscount';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { DiscountRetrieve } from 'types';
import { getUpdatedFieldKeys, SelectorMap } from 'utils/compare-obj-functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import AvailabilitySettingsSection from './AvailabilitySettingsSection';
import EligibleProductsSection, { EligibleProductsType } from './EligibleProductsSection';
import GeneralSettingsSection from './GeneralSettingsSection';

export interface FormValues {
  discountId: string;
  name: string;
  code: string;
  valueType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  amount: number | undefined;
  percentage: number | undefined;
  isActive: boolean;
  isOneTimeUse: boolean;
  eligibleProducts: string[] | [];
  eligibleProductsType: EligibleProductsType;
}

interface TransformedFormValues extends Omit<FormValues, 'amount' | 'percentage' | 'eligibleProductsType'> {
  amount: number | null;
  percentage: string | null;
}

interface IDrawerDiscountFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  discountId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const validateName = (value: string): string | null => {
  if (!value.trim()) return 'Name is required';
  if (value.length > 120) return 'Name must be 120 characters or less';
  return null;
};

const validateCode = (value: string): string | null => {
  if (!value) return 'Code is required';
  if (!/^[A-Za-z0-9_-]{8,24}$/.test(value)) {
    return 'Code must be 8–24 characters (letters, digits, - or _)';
  }
  return null;
};

const validateAmount = (value: number | undefined, values: FormValues): string | null => {
  if (values.valueType !== 'FIXED_AMOUNT') return null;
  if (!value || value <= 0) return 'Amount must be greater than 0';
  return null;
};

const validatePercentage = (value: number | undefined, values: FormValues): string | null => {
  if (values.valueType !== 'PERCENTAGE') return null;
  if (!value || value <= 0 || value > 100) return 'Percentage must be between 0 and 100';
  return null;
};

const validateEligibleProducts = (value: string[], values: FormValues): string | null => {
  if (values.eligibleProductsType === 'SPECIFIC' && value.length === 0) {
    return 'Select at least one Specific Product or switch to All Products';
  }
  return null;
};

const updatedFieldSelectors: SelectorMap = {
  name: obj => obj.name,
  is_active: obj => obj.isActive,
  is_one_time_use: obj => obj.isOneTimeUse,
  eligible_products: obj => [...(obj.eligibleProducts ?? [])].sort(),
};

const DrawerDiscountForm = ({
  opened,
  closeHandler,
  formId,
  title,
  discountId,
  type,
  permission = true,
}: IDrawerDiscountFormProps): JSX.Element => {
  const createMutation = useCreateDiscount();
  const updateMutation = useUpdateDiscount();
  const { data, isLoading, isFetching, isSuccess } = useDiscount(discountId);
  const { trackDiscountCreated, trackDiscountUpdated } = useStaffTracking();

  const isReadOnly = type === 'edit';

  const form = useForm<FormValues, (values: FormValues) => TransformedFormValues>({
    initialValues: {
      discountId: '',
      name: '',
      code: '',
      valueType: 'PERCENTAGE',
      amount: undefined,
      percentage: undefined,
      isActive: false,
      isOneTimeUse: true,
      eligibleProducts: [],
      eligibleProductsType: 'ALL',
    },
    validate: {
      name: validateName,
      code: validateCode,
      amount: validateAmount,
      percentage: validatePercentage,
      eligibleProducts: validateEligibleProducts,
    },
    transformValues: ({ eligibleProductsType, ...values }) => ({
      ...values,
      discountId: type === 'create' ? uuidv4() : values.discountId,
      amount: values.valueType === 'FIXED_AMOUNT' ? Math.round((values.amount ?? 0) * 100) : null,
      percentage: values.valueType === 'PERCENTAGE' ? String((values.percentage ?? 0) / 100) : null,
      eligibleProducts: eligibleProductsType === 'ALL' ? [] : values.eligibleProducts,
    }),
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleSubmit = (payload: TransformedFormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (type === 'edit') {
      const original = data?.data;
      updateMutation.mutate(
        {
          discountId: payload.discountId,
          name: payload.name,
          isActive: payload.isActive,
          isOneTimeUse: payload.isOneTimeUse,
          eligibleProducts: payload.eligibleProducts,
        },
        {
          onSuccess: response => {
            successNotification('Discount updated', 'Discount successfully updated.');
            const fieldChanged = getUpdatedFieldKeys(original, payload, updatedFieldSelectors);
            trackDiscountUpdated(payload.discountId, response.data.name, fieldChanged);
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification('Discount update failed', error.response?.data.message);
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: response => {
          successNotification('Discount created', `${response.data.name} successfully created.`);
          trackDiscountCreated(
            response.data.discountId,
            response.data.name,
            response.data.code,
            response.data.valueType,
            response.data.isActive,
            response.data.isOneTimeUse,
            (response.data.eligibleProducts ?? []).length,
          );
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Discount creation failed', error.response?.data.message);
        },
      });
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      const discount: DiscountRetrieve = data.data;
      const eligibleProducts = (discount.eligibleProducts ?? []).filter((id): id is string => id != null);
      form.setValues({
        discountId: discount.discountId,
        name: discount.name,
        code: discount.code,
        valueType: discount.valueType,
        amount: discount.amount != null ? discount.amount / 100 : undefined,
        percentage: discount.percentage != null ? parseFloat(discount.percentage) * 100 : undefined,
        isActive: discount.isActive,
        isOneTimeUse: discount.isOneTimeUse,
        eligibleProducts,
        eligibleProductsType: eligibleProducts.length > 0 ? 'SPECIFIC' : 'ALL',
      });
    }
  }, [data]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size='lg'
      title={<Title order={3}>{title}</Title>}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={!permission}
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          {type === 'create' ? (
            <Text size={'xs'}>
              Every discount you create will be linked to a unique code. Use them internally to close deals
              quickly at the POS, or distribute them via email to drive self-service sales through the
              Athlete App and Sales Portal
            </Text>
          ) : (
            ''
          )}
          <GeneralSettingsSection form={form} isReadOnly={isReadOnly} disabled={!permission} />

          <Divider />

          <EligibleProductsSection
            productsType={form.values.eligibleProductsType}
            selectedProducts={form.values.eligibleProducts}
            onTypeChange={value => form.setFieldValue('eligibleProductsType', value)}
            onSelectedProductsChange={value => form.setFieldValue('eligibleProducts', value)}
            disabled={!permission}
            error={form.errors.eligibleProducts as string | null | undefined}
          />

          <Divider />

          <AvailabilitySettingsSection form={form} disabled={!permission} />
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerDiscountForm;
